import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import './index.css';

// Initialize i18n (must be imported before App)
import './i18n/index';

import App from './App';
import ErrorBoundary from './components/common/ErrorBoundary';
import config from './config/environment';
import { POSTHOG_CONFIG, isAnalyticsEnabled } from './config/analytics';
import { captureAcquisitionSession } from './services/acquisitionSession';
import { handleAuthRedirectCallback } from './utils/authRedirect';
import { sanitizeConfigValue } from './utils/sanitizeConfigValue';
import { applyTheme, getSavedThemePreference } from './utils/theme';

// Initialize Sentry error tracking (must be before ReactDOM.createRoot)
const SENTRY_DSN = sanitizeConfigValue(process.env.REACT_APP_SENTRY_DSN);
if (SENTRY_DSN && process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    // Session replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    // Ignore specific errors
    ignoreErrors: [
      'ResizeObserver loop',
      'Non-Error promise rejection',
      'Network request failed',
    ],
  });
}

// Initialize PostHog analytics
if (isAnalyticsEnabled()) {
  posthog.init(POSTHOG_CONFIG.apiKey, {
    api_host: POSTHOG_CONFIG.apiHost,
    ...POSTHOG_CONFIG.options,
  });
}

// TRONA brand theme for Chakra UI
const tronaTheme = extendTheme({
  colors: {
    brand: {
      50: '#f4f2ff',
      100: '#e8e3ff',
      200: '#cfd7ff',
      300: '#b4aaff',
      400: '#8a4dff',
      500: '#6d5ef6',
      600: '#5848df',
      700: '#4636bd',
      800: '#322781',
      900: '#151827',
    },
  },
  fonts: {
    heading: `'Outfit', 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
    body: `'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
  },
  styles: {
    global: {
      html: {
        backgroundColor: '#FAFAFA !important',
      },
      'html.dark': {
        backgroundColor: '#0d0f1a !important',
      },
      body: {
        backgroundColor: '#FAFAFA !important',
        _dark: {
          backgroundColor: '#0d0f1a !important',
        },
      },
      'body.dark': {
        backgroundColor: '#0d0f1a !important',
      },
    },
  },
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
});

// Configure React Query client with smart defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2, // 2 minutes - data considered fresh
      gcTime: 1000 * 60 * 10, // 10 minutes - cache garbage collection (formerly cacheTime)
      retry: 2,
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when network reconnects
    },
    mutations: {
      retry: 1,
    },
  },
});

if (typeof window !== 'undefined') {
  applyTheme(getSavedThemePreference());
  window.__applyTheme = applyTheme;
  captureAcquisitionSession();
}

const onAuthRedirectCallback = (appState) => {
  handleAuthRedirectCallback(appState);
  captureAcquisitionSession();
};

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <QueryClientProvider client={queryClient}>
    <ChakraProvider theme={tronaTheme}>
      <Auth0Provider
        domain={config.auth0.domain}
        clientId={config.auth0.clientId}
        authorizationParams={{
          redirect_uri: config.auth0.redirectUri,
          audience: config.auth0.audience,
        }}
        cacheLocation="memory"
        useRefreshTokens={true}
        useRefreshTokensFallback={true}
        onRedirectCallback={onAuthRedirectCallback}
      >
        <BrowserRouter>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </BrowserRouter>
      </Auth0Provider>
    </ChakraProvider>
  </QueryClientProvider>
);
